import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Loader2, UserPlus, Users, Info, Check } from 'lucide-react';

const COPAR_PRICES = { basic: 60, plus: 60, premium: 100, gold: 100, platinum: 100, action: 100 };
const PLAN_NAMES = { basic: 'Vital Fit', plus: 'Vital Plus', premium: 'Vital Premium', gold: 'Vital Gold', platinum: 'Vital Platinum', action: 'Vital Action' };

const CoparticipationPage = () => {
  const { user } = useAuth();
  const [copars, setCopars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', cellphone: '', cpf: '', birth_date: '', cep: '', street: '', number: '', city: '', neighborhood: '' });

  const fetchData = useCallback(async () => {
    try {
      const coparsRes = await axios.get(`${API}/students/coparticipations`);
      setCopars(coparsRes.data || []);
    } catch { setCopars([]); }
    try {
      const planRes = await axios.get(`${API}/students/my-plan`);
      setPlan(planRes.data?.plan || null);
    } catch { setPlan(null); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const additional = COPAR_PRICES[plan] || 0;

  const handleSubmit = async () => {
    if (!form.name || !form.cpf) { toast.error('Nome e CPF são obrigatórios'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/students/coparticipation`, form);
      toast.success('Coparticipação cadastrada! Aguardando aprovação.');
      setForm({ name: '', email: '', cellphone: '', cpf: '', birth_date: '', cep: '', street: '', number: '', city: '', neighborhood: '' });
      setShowForm(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao cadastrar'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="coparticipation-page">
      <div className="flex items-start sm:items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Coparticipação</h1>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className={`${showForm ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`} data-testid="add-copar-btn">
          <UserPlus className="w-4 h-4 mr-1.5" />{showForm ? 'Cancelar' : 'Incluir'}
        </Button>
      </div>

      {plan && additional > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="copar-price-notice">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <strong>Valor adicional:</strong> Seu plano <strong>{PLAN_NAMES[plan] || plan}</strong> tem um adicional de <strong className="text-amber-900">R$ {additional.toFixed(2).replace('.', ',')}</strong> por cadastro de coparticipação. O pagamento será solicitado após aprovação do administrador.
          </div>
        </div>
      )}

      {showForm && (
        <Card className="border-emerald-200" data-testid="copar-form">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-bold text-slate-900">Dados do Coparticipante</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nome completo *" data-testid="copar-name" /></div>
              <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="E-mail" data-testid="copar-email" />
              <Input value={form.cellphone} onChange={e => setForm({...form, cellphone: e.target.value})} placeholder="Celular" data-testid="copar-cellphone" />
              <Input value={form.cpf} onChange={e => setForm({...form, cpf: e.target.value})} placeholder="CPF *" data-testid="copar-cpf" />
              <Input type="date" value={form.birth_date} onChange={e => setForm({...form, birth_date: e.target.value})} placeholder="Data de Nascimento" data-testid="copar-birth" />
              <Input value={form.cep} onChange={e => setForm({...form, cep: e.target.value})} placeholder="CEP" data-testid="copar-cep" />
              <Input value={form.street} onChange={e => setForm({...form, street: e.target.value})} placeholder="Rua" data-testid="copar-street" />
              <Input value={form.number} onChange={e => setForm({...form, number: e.target.value})} placeholder="Número" data-testid="copar-number" />
              <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Cidade" data-testid="copar-city" />
              <Input value={form.neighborhood} onChange={e => setForm({...form, neighborhood: e.target.value})} placeholder="Bairro" data-testid="copar-neighborhood" />
            </div>
            <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-copar-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}Cadastrar Coparticipação
            </Button>
          </CardContent>
        </Card>
      )}

      {copars.length === 0 ? (
        <Card className="border-slate-100"><CardContent className="p-8 text-center text-slate-500">Nenhuma coparticipação cadastrada</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {copars.map(c => (
            <Card key={c.copar_id} className={`overflow-hidden ${c.status === 'active' ? 'border-emerald-300 shadow-md' : 'border-slate-100'}`} data-testid={`copar-card-${c.copar_id}`}>
              {c.status === 'active' && (
                <div className="bg-emerald-600 px-4 py-2 flex items-center gap-2">
                  <Check className="w-4 h-4 text-white" />
                  <span className="text-sm font-semibold text-white">Coparticipação Ativa</span>
                  {c.activated_at && <span className="text-xs text-emerald-200 ml-auto">Ativado em {new Date(c.activated_at).toLocaleDateString('pt-BR')}</span>}
                </div>
              )}
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.status === 'active' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                      <Users className={`w-6 h-6 ${c.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.email || '-'} | {c.cellphone || '-'}</p>
                    </div>
                  </div>
                  {c.status !== 'active' && (
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-700">
                      Aguardando Aprovação
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid={`copar-details-${c.copar_id}`}>
                  <div><p className="text-[10px] uppercase font-semibold text-slate-400">CPF</p><p className="text-slate-700">{c.cpf || '-'}</p></div>
                  <div><p className="text-[10px] uppercase font-semibold text-slate-400">Nascimento</p><p className="text-slate-700">{c.birth_date || '-'}</p></div>
                  <div><p className="text-[10px] uppercase font-semibold text-slate-400">Celular</p><p className="text-slate-700">{c.cellphone || '-'}</p></div>
                  <div><p className="text-[10px] uppercase font-semibold text-slate-400">Cidade</p><p className="text-slate-700">{c.city || '-'}</p></div>
                  <div><p className="text-[10px] uppercase font-semibold text-slate-400">Bairro</p><p className="text-slate-700">{c.neighborhood || '-'}</p></div>
                  <div><p className="text-[10px] uppercase font-semibold text-slate-400">Endereço</p><p className="text-slate-700">{c.street ? `${c.street}, ${c.number || 'S/N'}` : '-'}</p></div>
                </div>

                {c.status === 'active' && c.access_code && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid={`copar-active-info-${c.copar_id}`}>
                      <div className="text-center sm:text-left">
                        <p className="text-[10px] uppercase font-semibold text-emerald-600">Plano</p>
                        <p className="text-sm font-bold text-emerald-800">{PLAN_NAMES[c.copar_plan] || c.copar_plan || 'Mesmo do titular'}</p>
                      </div>
                      <div className="text-center sm:text-left">
                        <p className="text-[10px] uppercase font-semibold text-emerald-600">Academia Vínculo</p>
                        <p className="text-sm font-bold text-emerald-800">{c.copar_gym_name || 'Mesma do titular'}</p>
                      </div>
                      <div className="text-center sm:text-left">
                        <p className="text-[10px] uppercase font-semibold text-emerald-600">Check-ins Extras</p>
                        <p className="text-sm font-bold text-emerald-800">{c.copar_extra_checkins || 0}/mês</p>
                      </div>
                    </div>
                    <div className="bg-slate-900 rounded-xl p-5 text-center" data-testid={`copar-code-box-${c.copar_id}`}>
                      <p className="text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Código de Acesso do Coparticipante</p>
                      <p className="text-3xl font-black text-emerald-400 tracking-[0.4em] font-mono" data-testid={`copar-access-code-${c.copar_id}`}>{c.access_code}</p>
                      <p className="text-[11px] text-slate-500 mt-2">Compartilhe este código para que o dependente acesse a plataforma</p>
                    </div>
                    {c.additional_value > 0 && (
                      <p className="text-xs text-slate-500 text-center">Valor adicional no plano: <strong>R$ {c.additional_value.toFixed(2).replace('.', ',')}</strong></p>
                    )}
                  </>
                )}

                {c.status !== 'active' && c.suggested_additional > 0 && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    Valor adicional estimado: <strong>R$ {c.suggested_additional.toFixed(2).replace('.', ',')}</strong> — será confirmado pelo administrador.
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoparticipationPage;
